import type { ReactNode } from "react";
import { Info, XCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { aiUsageApi } from "@/resources/ai-usage";
import { KpiCard } from "./KpiCard";
import { StackedBarChart } from "./StackedBarChart";
import { formatDuration, formatInteger, formatMoney } from "./ai-usage-format";

const KPI_COUNT = 6;
const SKELETON_ROWS = 5;

function SectionTitle({ title, tooltip }: { title: string; tooltip: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <h4 className="text-lg font-semibold text-foreground">{title}</h4>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label={`About ${title}`}>
            <Info className="h-4 w-4 text-muted-foreground" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-left">{tooltip}</TooltipContent>
      </Tooltip>
    </div>
  );
}

function CostCell({ cost, unpriced }: { cost: number; unpriced: number }) {
  return (
    <TableCell className="text-xs text-right tabular-nums">
      {formatMoney(cost)}
      {unpriced > 0 ? <span className="text-muted-foreground">{` · ${unpriced} unpriced`}</span> : null}
    </TableCell>
  );
}

function SkeletonRows({ columns, testId }: { columns: number; testId: string }) {
  return (
    <>
      {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
        <TableRow key={`${testId}-${i}`} data-testid={testId}>
          <TableCell colSpan={columns}>
            <Skeleton className="h-6 w-full" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

function TableCard({ title, tooltip, children }: { title: string; tooltip: string; children: ReactNode }) {
  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <SectionTitle title={title} tooltip={tooltip} />
        {children}
      </CardContent>
    </Card>
  );
}

// The design's TableHead is semibold foreground; ours is not, so the header carries it (as the Logs view does).
const HEAD = "text-xs font-semibold text-foreground";
const HEAD_NUM = `${HEAD} text-right`;

export function AiUsageSummaryView() {
  const { data, isLoading, isError } = aiUsageApi.useGetAiUsageSummary();
  const hasLoadError = isError && !data;

  if (hasLoadError) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Couldn&apos;t load AI usage</AlertTitle>
          <AlertDescription>Something went wrong loading AI usage. Try again in a moment.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const totals = data?.totals;
  const loading = isLoading || !data || !totals;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          Array.from({ length: KPI_COUNT }).map((_, i) => (
            <Skeleton key={i} data-testid="ai-usage-kpi-skeleton" className="h-[88px] w-full" />
          ))
        ) : (
          <>
            <KpiCard
              label="Cost today"
              value={formatMoney(totals.cost_today)}
              tooltip="AI spend across every feature so far today."
              secondLine={totals.unpriced_today > 0 ? `Includes ${totals.unpriced_today} unpriced call(s)` : undefined}
            />
            <KpiCard
              label="Cost this month"
              value={formatMoney(totals.cost_window)}
              tooltip="AI spend for the trailing 30 days."
              secondLine={totals.unpriced_window > 0 ? `Includes ${totals.unpriced_window} unpriced call(s)` : undefined}
            />
            <KpiCard
              label="Total tokens"
              value={formatInteger(totals.tokens_window)}
              tooltip="Input plus output tokens across all logged calls."
            />
            <KpiCard
              label="Requests today"
              value={formatInteger(totals.requests_today)}
              tooltip="Number of AI calls made today."
            />
            <KpiCard
              label="Errors today"
              value={formatInteger(totals.errors_today)}
              tooltip="AI calls that failed today."
            />
            <KpiCard
              label="Avg latency"
              value={formatDuration(totals.avg_latency_ms ?? 0)}
              tooltip="Mean end-to-end duration of an AI call."
            />
          </>
        )}
      </div>

      <Card>
        <CardContent className="p-6 space-y-2">
          <SectionTitle
            title="Daily cost by model"
            tooltip="Daily AI spend for the trailing 30 days, stacked by model."
          />
          <div className="text-xs text-muted-foreground">Last 30 days</div>
          <div className="pt-4">
            {loading ? (
              <Skeleton data-testid="ai-usage-chart-skeleton" className="h-[320px] w-full" />
            ) : (
              <StackedBarChart
                seriesLabels={data.models}
                days={data.daily.map((d) => ({ date: d.date, values: d.cost_by_model, unpriced: d.unpriced }))}
              />
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TableCard
          title="Usage by model"
          tooltip="Requests, tokens and spend grouped by the model that served the call."
        >
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead className={HEAD}>Model</TableHead>
                <TableHead className={HEAD_NUM}>Requests</TableHead>
                <TableHead className={HEAD_NUM}>Tokens</TableHead>
                <TableHead className={HEAD_NUM}>Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <SkeletonRows columns={4} testId="ai-usage-model-row-skeleton" />
              ) : (
                data.by_model.map((row) => (
                  <TableRow key={row.model}>
                    <TableCell className="text-xs font-medium text-foreground">{row.model}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{formatInteger(row.requests)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{formatInteger(row.tokens)}</TableCell>
                    <CostCell cost={row.cost} unpriced={row.unpriced} />
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableCard>

        <TableCard
          title="Usage by feature"
          tooltip="Requests and spend grouped by the product surface that triggered the call."
        >
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead className={HEAD}>Feature</TableHead>
                <TableHead className={HEAD_NUM}>Requests</TableHead>
                <TableHead className={HEAD_NUM}>Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <SkeletonRows columns={3} testId="ai-usage-feature-row-skeleton" />
              ) : (
                data.by_feature.map((row) => (
                  <TableRow key={row.feature}>
                    <TableCell className="text-xs font-medium text-foreground">{row.feature}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{formatInteger(row.requests)}</TableCell>
                    <CostCell cost={row.cost} unpriced={row.unpriced} />
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableCard>
      </div>
    </div>
  );
}
